import { useEffect, useRef, useState } from 'react';
import ApiDatabase from 'server';

export const POLL_MS = 2000;

/**
 * Follows a run of the invoice import until it ends (JBWEB-1266): read again
 * every two seconds while it runs, then `onFinished` once.
 *
 * Two screens following the same run at once (the page's toast and the
 * invoice detail, JBWEB-1274) need their own `abortKey`: on the same key, each
 * read cancels the other's, and a cancelled read stops the polling.
 */
const useInvoiceImportRun = (runId, onFinished, { abortKey } = {}) => {
  const [run, setRun] = useState(null);
  const onFinishedRef = useRef(onFinished);
  onFinishedRef.current = onFinished;

  useEffect(() => {
    if (!runId) return undefined;
    let active = true;
    let timer = null;
    setRun(null);

    const options = abortKey ? [{ abortKey }] : [];
    const poll = () => {
      ApiDatabase.getInvoiceImportRun(
        { runId },
        (data) => {
          if (!active) return;
          const next = data?.run ?? null;
          setRun(next);
          if (next && next.status !== 'running') {
            onFinishedRef.current?.(next);
            return;
          }
          timer = setTimeout(poll, POLL_MS);
        },
        (error) => {
          if (!active || error?.name === 'CanceledError') return;
          // A run that no longer exists is not waited for; a failed read is retried.
          if (error?.response?.status === 404) return;
          timer = setTimeout(poll, POLL_MS);
        },
        ...options,
      );
    };
    poll();

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [runId, abortKey]);

  return run;
};

export default useInvoiceImportRun;
